import type { PackClass, PackWord } from './types';

/**
 * Class membership for a word. A word has one primary `class` (used for display and decoy
 * bookkeeping) and may declare extra `classes` it ALSO belongs to. This models real overlaps:
 * "Maus" is a `creature` (Wesen) AND a `noun` (Nomen), so it's a valid target on both the
 * Wesen board and the Nomen board — and is never a wrong answer on a Nomen board. The engine
 * stays taxonomy-agnostic; it only knows a word can belong to several class ids.
 * See specs/fables-lesson.md.
 */

/** Every class id a word belongs to: its primary `class` plus any extra `classes` (deduped). */
export function wordClasses(word: PackWord): string[] {
  if (!word.classes || word.classes.length === 0) return [word.class];
  return word.classes.includes(word.class) ? [...word.classes] : [word.class, ...word.classes];
}

/** True if `word` belongs to `classId` via its primary class or any extra membership. */
export function wordInClass(word: PackWord, classId: string): boolean {
  return word.class === classId || (!!word.classes && word.classes.includes(classId));
}

/**
 * True if `word` may be shown as a WRONG answer while `cls` is the target: it must not belong to
 * the target class, and not to any class the target declares confusable via `decoyExcludeClasses`
 * (an apple grows on a plant, so "Apfel" is an unfair distractor on the Natur board).
 * Both board builders — ReadingProgression.buildBoard and SurvivalScene.buildPools — go through
 * here, or the same word would be a fair decoy in one mode and a run-ending trap in the other.
 */
export function canBeDecoy(word: PackWord, cls: Pick<PackClass, 'id' | 'decoyExcludeClasses'>): boolean {
  if (wordInClass(word, cls.id)) return false;
  const excluded = cls.decoyExcludeClasses;
  if (!excluded || excluded.length === 0) return true;
  return !wordClasses(word).some((c) => excluded.includes(c));
}
